import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Button, Chip, CircularProgress, Container, Grid, InputAdornment,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, useTheme, Tab, Tabs, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel,
  Divider, Avatar, Tooltip, IconButton, Badge,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SearchIcon from "@mui/icons-material/Search";
import GavelIcon from "@mui/icons-material/Gavel";
import WorkIcon from "@mui/icons-material/Work";
import PaymentsIcon from "@mui/icons-material/Payments";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import AdminApi from "Service/Admin.api";
import ColorData from "Components/CstmTheme/ColorData";
import { StyledTableCell, StyledTableRow } from "./ProfessionalUserList";

// ─── Type Definitions ────────────────────────────────────────────────────────

interface AssignedProfessional {
  professionalId: number;
  fullName: string;
  email: string;
  contactNumber: string;
  role: string;
  consentStatus: string;
  payoutAmount?: number;
}

interface CaseInfo {
  pkCase: number;
  caseNumber: string;
  orderTypeName: string;
  orderTypeId: number;
  currentProcessingStatusName: string;
  assignedProfessionals?: AssignedProfessional[];
}

interface ProfessionalUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  city: string;
  state: string;
  highestQualification: string;
  description: string;
  roles?: string[];
  skills?: { skillName: string; numberOfExperienceYears: number }[];
}

// ─── Auxiliary role options ───────────────────────────────────────────────────
const PANEL_ROLE_OPTIONS = [
  "Primary Lawyer",
  "Co-Mediator",
  "Co-Arbitrator",
  "Legal Advisor",
  "Subject Matter Expert",
  "Technical Expert",
  "Document Reviewer",
  "Financial Expert",
  "Panel Member",
];

const ODR_TYPES = ["Online Mediation", "Online Arbitration", "Arbitration", "Online Negotiation"];

// ─── Styled helpers ───────────────────────────────────────────────────────────
const panelBorderStyle = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  p: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  backgroundColor: "background.paper",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  transition: "box-shadow 0.2s",
  "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
};

// ─── Component ────────────────────────────────────────────────────────────────
const ManageCasePanel: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  // Case data
  const [caseInfo, setCaseInfo] = useState<CaseInfo | null>(null);

  // Professional lists
  const [odrNeutrals, setOdrNeutrals] = useState<ProfessionalUser[]>([]);
  const [nlpProfessionals, setNlpProfessionals] = useState<ProfessionalUser[]>([]);

  // UI state
  const [activeTab, setActiveTab] = useState(0); // 0 = ODR Neutrals, 1 = NLP Professionals
  const [searchQuery, setSearchQuery] = useState("");
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedProf, setSelectedProf] = useState<ProfessionalUser | null>(null);
  const [selectedRole, setSelectedRole] = useState("Panel Member");
  const [payoutAmount, setPayoutAmount] = useState<string>("");
  const [isForceAux, setIsForceAux] = useState(false); // derived from activeTab, kept for dialog message
  const [removeLoading, setRemoveLoading] = useState<Record<number, boolean>>({});

  const isOdrCase = useMemo(
    () => ODR_TYPES.includes(caseInfo?.orderTypeName ?? ""),
    [caseInfo]
  );

  // ── Data Fetching ────────────────────────────────────────────────────────────

  const fetchCaseDetails = useCallback(async () => {
    if (!caseId) return;
    dispatch(startLoading({ loading: true, loadtext: "Loading case panel..." }));
    try {
      const res = await AdminApi.fetchCaseDetails(Number(caseId));
      if (res?.success) {
        setCaseInfo(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(stopLoading());
    }
  }, [caseId, dispatch]);

  const fetchProfessionals = useCallback(async () => {
    try {
      // Fetch all active & verified professionals in one call.
      // The API returns each professional's `roles` array, so we can split client-side.
      const res = await AdminApi.GetAllActiveProfessionalUser();
      const allActive: ProfessionalUser[] = res?.success && Array.isArray(res.data) ? res.data : [];

      // ODR Neutrals = professionals who hold at least one ODR-specific role
      const ODR_ROLES = ["mediator", "arbitrator", "negotiator"];
      const odrPool = allActive.filter((u) =>
        u.roles?.some((r: string) => ODR_ROLES.includes(r.toLowerCase()))
      );

      // NLP Professionals = ALL active verified professionals (any role)
      setOdrNeutrals(odrPool);
      setNlpProfessionals(allActive);
    } catch (err) {
      console.error("Error loading professionals:", err);
    }
  }, []);

  useEffect(() => {
    fetchCaseDetails();
    fetchProfessionals();
  }, [fetchCaseDetails, fetchProfessionals]);

  // ── Search Filtering ─────────────────────────────────────────────────────────

  const searchList = activeTab === 0 ? odrNeutrals : nlpProfessionals;

  const filteredList = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return searchList;
    return searchList.filter((u) => {
      const name = `${u.firstName} ${u.lastName}`.toLowerCase();
      const skills = u.skills?.map((s) => s.skillName).join(" ").toLowerCase() ?? "";
      const roles = u.roles?.join(" ").toLowerCase() ?? "";
      return (
        name.includes(q) ||
        (u.email ?? "").toLowerCase().includes(q) ||
        (u.contactNumber ?? "").toLowerCase().includes(q) ||
        skills.includes(q) ||
        roles.includes(q) ||
        String(u.id).includes(q)
      );
    });
  }, [searchQuery, searchList]);

  // ── Assign Professional ──────────────────────────────────────────────────────

  const openAssignDialog = (prof: ProfessionalUser) => {
    setSelectedProf(prof);
    // Tab 1 = NLP Professionals (auxiliary/force-assign)
    const isAux = activeTab === 1;
    setIsForceAux(isAux);
    setSelectedRole(isAux ? "Panel Member" : (isOdrCase ? "Primary Neutral" : "Primary Lawyer"));
    setPayoutAmount("");
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedProf || !caseId) return;
    setAssignDialogOpen(false);
    dispatch(startLoading({ loading: true, loadtext: "Assigning to panel..." }));
    try {
      // Tab 0 = ODR Neutral (uses standard ODR role check, no force-aux)
      // Tab 1 = NLP Professional (force-aux = always insert to CaseProfessionals, skip ODR role check)
      const res = await AdminApi.AssignCaseToProfessional({
        caseId: Number(caseId),
        assignedProfessionalId: selectedProf.id,
        requestedRole: selectedRole,
        payoutAmount: payoutAmount ? parseFloat(payoutAmount) : undefined,
        forceAssignAuxiliary: activeTab === 1, // NLP tab always bypasses ODR role check
      });

      dispatch(stopLoading());
      dispatch(setAlertData({
        alertMessage: res?.message ?? (res?.success ? "Professional added to panel." : "Failed to assign."),
        alertVariant: res?.success ? "success" : "error",
        openAlert: true,
      }));
      if (res?.success) fetchCaseDetails();
    } catch (err: any) {
      dispatch(stopLoading());
      dispatch(setAlertData({ alertMessage: err?.message ?? "Error assigning professional.", alertVariant: "error", openAlert: true }));
    }
  };

  // ── Remove Professional ──────────────────────────────────────────────────────

  const handleRemove = async (profId: number) => {
    if (!caseId) return;
    setRemoveLoading((prev) => ({ ...prev, [profId]: true }));
    try {
      const res = await AdminApi.UnassignCaseProfessional({ caseId: Number(caseId), professionalId: profId });
      dispatch(setAlertData({
        alertMessage: res?.message ?? (res?.success ? "Professional removed from panel." : "Failed to remove."),
        alertVariant: res?.success ? "success" : "error",
        openAlert: true,
      }));
      if (res?.success) fetchCaseDetails();
    } catch (err: any) {
      dispatch(setAlertData({ alertMessage: err?.message ?? "Error removing professional.", alertVariant: "error", openAlert: true }));
    } finally {
      setRemoveLoading((prev) => ({ ...prev, [profId]: false }));
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 }, minHeight: "100vh", backgroundColor: "#f7f8fc" }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2, fontWeight: 600, textTransform: "none" }}
      >
        Back
      </Button>

      {/* Page Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
        <GroupsIcon sx={{ fontSize: 34, color: ColorData.primary.main }} />
        <Typography variant="h5" fontWeight={700}>
          Manage Professional Panel
        </Typography>
      </Box>
      <Typography variant="body2" color="text.primary" mb={3}>
        Assign and manage multiple professionals for this case. ODR Neutrals are role-restricted; Panel Members can be any verified professional.
      </Typography>

      {/* Case Info Banner */}
      {caseInfo && (
        <Paper
          sx={{
            mb: 3, p: 2, borderRadius: 2,
            background: `linear-gradient(135deg, ${ColorData.primary.main}18, ${ColorData.primary.main}08)`,
            border: `1px solid ${ColorData.primary.main}30`,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.primary">Case Number</Typography>
              <Typography variant="h6" fontWeight={700}>{caseInfo.caseNumber}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="subtitle2" color="text.primary">Order Type</Typography>
              <Chip
                label={caseInfo.orderTypeName}
                size="small"
                color={isOdrCase ? "info" : "primary"}
                variant="outlined"
                sx={{ mt: 0.5, fontWeight: 600 }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="subtitle2" color="text.primary">Status</Typography>
              <Chip label={caseInfo.currentProcessingStatusName} size="small" color="info" sx={{ mt: 0.5 }} />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Current Panel Section */}
      <Paper sx={{ mb: 3, p: 3, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
        <Typography variant="h6" fontWeight={700} mb={2} display="flex" alignItems="center" gap={1}>
          <Badge
            badgeContent={caseInfo?.assignedProfessionals?.length ?? 0}
            color="primary"
          >
            <GroupsIcon />
          </Badge>
          &nbsp; Current Panel Members
        </Typography>

        {(!caseInfo?.assignedProfessionals || caseInfo.assignedProfessionals.length === 0) ? (
          <Box
            sx={{
              p: 4, textAlign: "center", borderRadius: 2,
              backgroundColor: "#f5f5f5", border: "1px dashed #ccc",
            }}
          >
            <GroupsIcon sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
            <Typography color="text.primary">No professionals assigned to this case yet.</Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {caseInfo.assignedProfessionals.map((prof) => (
              <Box key={prof.professionalId} sx={panelBorderStyle}>
                <Box display="flex" alignItems="center" gap={2} flex={1}>
                  <Avatar sx={{ bgcolor: ColorData.primary.main, width: 44, height: 44 }}>
                    {prof.fullName?.charAt(0) ?? "P"}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={700}>{prof.fullName}</Typography>
                    <Typography variant="body2" color="text.primary">
                      {prof.email} &bull; {prof.contactNumber}
                    </Typography>
                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                      <Chip
                        label={prof.role || "Professional"}
                        size="small"
                        variant="outlined"
                        color="primary"
                        icon={<WorkIcon />}
                      />
                      <Chip
                        label={prof.consentStatus || "Assigned"}
                        size="small"
                        color={
                          prof.consentStatus === "Confirmed" || prof.consentStatus === "Accepted"
                            ? "success"
                            : prof.consentStatus === "Declined"
                              ? "error"
                              : "warning"
                        }
                      />
                      {prof.payoutAmount != null && (
                        <Chip
                          label={`Payout: ₹${prof.payoutAmount.toLocaleString("en-IN")}`}
                          size="small"
                          icon={<PaymentsIcon />}
                          variant="outlined"
                          color="success"
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                <Tooltip title="Remove from panel">
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={removeLoading[prof.professionalId] ? <CircularProgress size={14} /> : <PersonRemoveIcon />}
                      disabled={removeLoading[prof.professionalId]}
                      onClick={() => handleRemove(prof.professionalId)}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Remove
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Add Professional Section */}
      <Paper sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", overflow: "hidden" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, pt: 2, pb: 0 }}>
          <Typography variant="h6" fontWeight={700} mb={1.5} display="flex" alignItems="center" gap={1}>
            <PersonAddIcon color="primary" /> Add a Professional to the Panel
          </Typography>
          <Tabs
            value={activeTab}
            onChange={(_, v) => { setActiveTab(v); setSearchQuery(""); }}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              icon={<GavelIcon fontSize="small" />}
              label="ODR Neutrals"
              iconPosition="start"
              sx={{ fontWeight: 600, textTransform: "none" }}
            />
            <Tab
              icon={<WorkIcon fontSize="small" />}
              label="NLP Professionals"
              iconPosition="start"
              sx={{ fontWeight: 600, textTransform: "none" }}
            />
          </Tabs>
        </Box>

        <Box px={3} pt={2} pb={1}>
          {activeTab === 0 && (
            <Box
              sx={{
                mb: 2, p: 1.5, borderRadius: 2, backgroundColor: "#e8f4ff",
                border: "1px solid #90caf9", display: "flex", alignItems: "center", gap: 1,
              }}
            >
              <InfoOutlinedIcon color="info" fontSize="small" />
              <Typography variant="body2" color="info.dark">
                ODR Neutrals are professionals with verified Mediator / Arbitrator / Negotiator roles.
                Assigning them uses the standard ODR workflow with consent tracking.
              </Typography>
            </Box>
          )}
          {activeTab === 1 && (
            <Box
              sx={{
                mb: 2, p: 1.5, borderRadius: 2, backgroundColor: "#fff8e1",
                border: "1px solid #ffe082", display: "flex", alignItems: "center", gap: 1,
              }}
            >
              <InfoOutlinedIcon sx={{ color: "#f9a825" }} fontSize="small" />
              <Typography variant="body2" sx={{ color: "#795548" }}>
                These professionals will be added as <strong>auxiliary panel members</strong> (bypassing ODR role checks).
                You can assign them a custom role and payout amount.
              </Typography>
            </Box>
          )}

          {/* Search */}
          <TextField
            fullWidth
            placeholder={`Search ${activeTab === 0 ? "ODR neutrals" : "NLP professionals"}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />

          {/* Professional Table */}
          <TableContainer sx={{ maxHeight: 420, overflowY: "auto", borderRadius: 2, border: "1px solid #e0e0e0" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <StyledTableCell>ID</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Contact</StyledTableCell>
                  <StyledTableCell>Qualification</StyledTableCell>
                  <StyledTableCell>Skills / Roles</StyledTableCell>
                  <StyledTableCell align="center">Action</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <Typography color="text.primary">No professionals found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredList.map((prof) => {
                    const isAlreadyAssigned = caseInfo?.assignedProfessionals?.some(
                      (ap) => ap.professionalId === prof.id
                    );
                    return (
                      <StyledTableRow key={prof.id}>
                        <TableCell>{prof.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {prof.firstName} {prof.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.primary">
                            {prof.city}{prof.state ? `, ${prof.state}` : ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">{prof.contactNumber}</Typography>
                          <Typography variant="caption" color="text.primary">{prof.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{prof.highestQualification}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {prof.skills?.slice(0, 2).map((s) => (
                              <Chip key={s.skillName} label={s.skillName} size="small" variant="outlined" />
                            ))}
                            {prof.roles?.map((r) => (
                              <Chip key={r} label={r} size="small" color="primary" variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {isAlreadyAssigned ? (
                            <Chip label="Assigned" color="success" size="small" />
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PersonAddIcon />}
                              onClick={() => openAssignDialog(prof)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                background: ColorData.primary.main,
                                color: "white",
                                "&:hover": { background: ColorData.hover.main },
                              }}
                            >
                              Add to Panel
                            </Button>
                          )}
                        </TableCell>
                      </StyledTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box pb={2} />
      </Paper>

      {/* Assign Confirmation Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <PersonAddIcon color="primary" />
          Confirm Panel Assignment
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {selectedProf && (
            <>
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: "#f5f5f5", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {selectedProf.firstName} {selectedProf.lastName}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {selectedProf.email} &bull; {selectedProf.contactNumber}
                </Typography>
              </Box>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Role on this Case</InputLabel>
                <Select
                  value={selectedRole}
                  label="Role on this Case"
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  {PANEL_ROLE_OPTIONS.map((r) => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Manual Payout Amount (₹) — Optional"
                type="number"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                helperText="Leave blank if payout is not yet determined."
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />

              {isForceAux && (
                <Box
                  sx={{
                    mt: 2, p: 1.5, borderRadius: 2,
                    backgroundColor: "#fff8e1", border: "1px solid #ffe082",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#795548" }}>
                    This professional will be added as an <strong>auxiliary panel member</strong>.
                    They will have full access to the case chat, including private caucuses.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setAssignDialogOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            sx={{
              textTransform: "none", fontWeight: 700,
              background: ColorData.primary.main, color: ColorData.text.primary,
              "&:hover": { background: ColorData.hover.main },
            }}
          >
            Confirm Assignment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageCasePanel;
